import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/admin/layout/Layout';
import Table from '../components/shared/Table';
import Button from '../components/shared/Button';
import Input from '../components/shared/Input';
import Modal from '../components/shared/Modal';
import { adminApi } from '../api/admin';
import type { Project } from '../types';
import type { ApiError } from '../api/client';

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.projects.list();
      setProjects(data);
    } catch (err) {
      setError((err as ApiError).message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Projects';
    return () => { document.title = 'VR Companion Admin'; };
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const filteredProjects = projects.filter(
    (p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openCreate = () => {
    setEditingProject(null);
    setFormName('');
    setFormDescription('');
    setIsModalOpen(true);
  };

  const openEdit = (p: Project) => {
    setEditingProject(p);
    setFormName(p.name);
    setFormDescription(p.description || '');
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formName.trim()) return;
    setSubmitLoading(true);
    try {
      if (editingProject) {
        await adminApi.projects.update(editingProject.id, {
          name: formName.trim(),
          description: formDescription.trim() || undefined,
        });
      } else {
        await adminApi.projects.create({
          name: formName.trim(),
          description: formDescription.trim() || undefined,
        });
      }
      setIsModalOpen(false);
      fetchProjects();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to save');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (p: Project) => {
    if (!window.confirm(`Delete project "${p.name}"?`)) return;
    try {
      await adminApi.projects.delete(p.id);
      fetchProjects();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to delete');
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(
      () => { /* optional: toast or state */ },
      () => {}
    );
  };

  const columns = [
    {
      header: 'Project Name',
      accessor: (row: Project) => (
        <div>
          <div className="font-medium text-gray-900">{row.name}</div>
          {row.description && (
            <div className="text-sm text-gray-500">{row.description}</div>
          )}
        </div>
      ),
    },
    {
      header: 'Project ID',
      accessor: (row: Project) => (
        <div className="flex items-center gap-2">
          <code className="project-id-code text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded truncate max-w-[280px]" title={row.id}>
            {row.id}
          </code>
          <button
            type="button"
            className="text-primary-600 hover:text-primary-900 text-sm font-medium whitespace-nowrap table-action-btn"
            onClick={() => copyToClipboard(row.id, 'Project ID')}
          >
            Copy
          </button>
        </div>
      ),
    },
    {
      header: 'Actions',
      accessor: (row: Project) => (
        <div className="flex space-x-3">
          <button
            type="button"
            className="text-primary-600 hover:text-primary-900 text-sm font-medium table-action-btn"
            onClick={() => navigate(`/projects/${row.id}/floorplan`)}
          >
            Floor Plan
          </button>
          <button
            type="button"
            className="text-primary-600 hover:text-primary-900 text-sm font-medium"
            onClick={() => openEdit(row)}
          >
            Edit
          </button>
          <button
            type="button"
            className="text-red-600 hover:text-red-900 text-sm font-medium"
            onClick={() => handleDelete(row)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <Layout pageTitle="Projects">
      <div className="space-y-6">
        {error && (
          <div className="error-alert">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
        <div className="admin-toolbar">
          <div className="admin-toolbar-primary">
            <Input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="admin-toolbar-actions">
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`px-4 py-2 text-sm font-medium ${
                  viewMode === 'table'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Table
              </button>
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 text-sm font-medium border-l border-gray-300 ${
                  viewMode === 'grid'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Grid
              </button>
            </div>
            <Button onClick={openCreate}>Create Project</Button>
          </div>
        </div>

        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : viewMode === 'table' ? (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <Table columns={columns} data={filteredProjects} />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredProjects.map((project) => (
              <div key={project.id} className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
                {project.description && (
                  <p className="text-sm text-gray-600 mt-2">{project.description}</p>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-500">Project ID:</span>
                  <code className="project-id-code text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded flex-1 truncate" title={project.id}>
                    {project.id}
                  </code>
                  <button
                    type="button"
                    className="text-primary-600 hover:text-primary-900 text-sm font-medium table-action-btn"
                    onClick={() => copyToClipboard(project.id, 'Project ID')}
                  >
                    Copy
                  </button>
                </div>
                <div className="mt-4 flex space-x-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(project)}>
                    Edit
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(project)}>
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={editingProject ? 'Edit Project' : 'Create New Project'}
          footer={
            <>
              <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={submitLoading || !formName.trim()}>
                {submitLoading ? 'Saving...' : editingProject ? 'Update' : 'Create Project'}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {editingProject && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project ID (for Unreal / Control Panel)</label>
                <div className="flex items-center gap-2">
                  <code className="project-id-code flex-1 text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded truncate">
                    {editingProject.id}
                  </code>
                  <button
                    type="button"
                    className="text-primary-600 hover:text-primary-900 text-sm font-medium table-action-btn"
                    onClick={() => copyToClipboard(editingProject.id, 'Project ID')}
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
            <Input
              label="Project Name"
              placeholder="Enter project name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Enter project description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
              />
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export default Projects;
